import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Music, FileText, Loader2, Check, MapPin, DoorOpen,
  CalendarClock, X, MessageSquare, Mail, Phone, User, Euro, ExternalLink,
} from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { useActionBarConfig } from '@/contexts/ActionBarContext';
import { writeSelectedRecord } from '@/lib/selectedRecord';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import TeacherAvailabilityTab from './TeacherAvailabilityTab';
import PersonRemindersPanel from '@/pages/Dashboard/PersonRemindersPanel';
import { matchesFreeText } from '@/components/search/SearchBuilder';
import PersonAppKennungen from '@/components/app/PersonAppKennungen';
import PhoneLink from '@/components/PhoneLink';
import EmailLink from '@/components/EmailLink';

const emptyForm = (t) => ({
  first_name: t?.first_name || '',
  last_name: t?.last_name || '',
  email: t?.email || '',
  phone: t?.phone || '',
  address: t?.address || '',
  postal_code: t?.postal_code || '',
  city: t?.city || '',
  qualifications: t?.qualifications || '',
  hourly_rate: t?.hourly_rate != null ? String(t.hourly_rate) : '',
  status: t?.status || 'active',
  notes: t?.notes || '',
});

export default function TeacherDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [form, setForm] = useState(emptyForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMaster, setSavingMaster] = useState(false);
  const [payrollProfile, setPayrollProfile] = useState(null);

  useActionBarConfig({
    selected: teacher ? {
      kind: 'teacher', id: teacher.id,
      name: `${teacher.first_name} ${teacher.last_name}`,
      email: teacher.email, phone: teacher.phone,
    } : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacher?.id]);

  useEffect(() => {
    if (teacher?.id) writeSelectedRecord({ teacherId: teacher.id });
  }, [teacher?.id]);

  // Master data
  const [allInstruments, setAllInstruments] = useState([]);  // [{name, subject_count, subject_ids, subject_names}]
  const [allTariffs, setAllTariffs] = useState([]);
  const [locationsUsage, setLocationsUsage] = useState(null); // {total_courses, locations:[{...rooms[]}]}

  // Assignments
  const [assignedInstruments, setAssignedInstruments] = useState(new Set());
  const [assignedTariffIds, setAssignedTariffIds] = useState(new Set());

  // Search filters
  const [instrumentSearch, setInstrumentSearch] = useState('');
  const [tariffSearch, setTariffSearch] = useState('');

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const loadTeacher = useCallback(async () => {
    try {
      setLoading(true);
      const [teacherRes, instrumentsRes, teacherInstrumentsRes, allInstrumentsRes, tariffsRes, assignmentsRes] = await Promise.all([
        api.get(`/teachers/${id}`),
        api.get(`/teachers/subjects/instruments`),
        api.get(`/teachers/${id}/instruments`),
        api.get(`/teachers/subjects/instruments`),
        api.get('/tariffs-discounts/tariffs'),
        api.get(`/teachers/${id}/assignments`),
      ]);

      setTeacher(teacherRes.data);
      setForm(emptyForm(teacherRes.data));
      setAllInstruments(instrumentsRes.data);
      setAllTariffs(tariffsRes.data);
      setAssignedInstruments(new Set(teacherInstrumentsRes.data.instruments));
      setAssignedTariffIds(new Set(assignmentsRes.data.tariff_ids));
      void allInstrumentsRes;
      api.get(`/teachers/${id}/locations`).then(r => setLocationsUsage(r.data)).catch(() => {});
      api.get('/payroll/profiles')
        .then((r) => {
          const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          setPayrollProfile(list.find((p) => String(p.teacher_id) === String(id)) || null);
        })
        .catch(() => setPayrollProfile(null));
    } catch (error) {
      toast.error('Fehler beim Laden des Lehrers');
      navigate('/teachers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadTeacher();
  }, [loadTeacher]);

  const toggleInstrument = (name) => {
    setAssignedInstruments(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleTariff = (tariffId) => {
    setAssignedTariffIds(prev => {
      const next = new Set(prev);
      if (next.has(tariffId)) next.delete(tariffId); else next.add(tariffId);
      return next;
    });
  };

  const saveMasterData = async () => {
    if (!(form.first_name || '').trim() || !(form.last_name || '').trim()) {
      toast.error('Vor- und Nachname sind Pflicht');
      return;
    }
    if (!(form.email || '').trim()) {
      toast.error('E-Mail ist Pflicht');
      return;
    }
    setSavingMaster(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: (form.phone || '').trim() || null,
        address: (form.address || '').trim() || null,
        postal_code: (form.postal_code || '').trim() || null,
        city: (form.city || '').trim() || null,
        qualifications: (form.qualifications || '').trim() || null,
        notes: (form.notes || '').trim() || null,
        status: form.status || 'active',
        hourly_rate: form.hourly_rate === '' || form.hourly_rate == null
          ? null
          : parseFloat(String(form.hourly_rate).replace(',', '.')),
      };
      const r = await api.put(`/teachers/${id}`, payload);
      setTeacher(r.data);
      setForm(emptyForm(r.data));
      toast.success('Stammdaten gespeichert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingMaster(false);
    }
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      const res = await Promise.all([
        api.put(`/teachers/${id}/instruments`, { instruments: Array.from(assignedInstruments) }),
        api.put(`/teachers/${id}/tariffs`, { tariff_ids: Array.from(assignedTariffIds) })
      ]);
      const subjectCount = res[0]?.data?.subject_count ?? 0;
      toast.success(`Gespeichert: ${assignedInstruments.size} Instrumente (${subjectCount} Unterrichtsformen)`);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const filteredInstruments = allInstruments.filter(i =>
    matchesFreeText(i, instrumentSearch)
  );

  const filteredTariffs = allTariffs.filter(t =>
    matchesFreeText(t, tariffSearch)
  );

  const totalSubjectsForAssigned = filteredInstruments
    .filter(i => assignedInstruments.has(i.name))
    .reduce((sum, i) => sum + i.subject_count, 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/teachers')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900">
              {teacher?.first_name} {teacher?.last_name}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
              {teacher?.email && (
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-slate-400 shrink-0" />
                  <EmailLink email={teacher.email} data-testid="teacher-detail-email" />
                </span>
              )}
              {teacher?.phone ? (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-slate-400 shrink-0" />
                  <PhoneLink phone={teacher.phone} data-testid="teacher-detail-phone" />
                </span>
              ) : (
                <span className="text-slate-400">Kein Telefon</span>
              )}
            </div>
          </div>
          <Badge className={teacher?.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
            {teacher?.status === 'active' ? 'Aktiv' : 'Inaktiv'}
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="stammdaten" className="space-y-4 w-full min-w-0">
          <TabsList className="bg-slate-100 flex h-auto w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="stammdaten" className="data-[state=active]:bg-white shrink-0" data-testid="tab-stammdaten">
              <User className="h-4 w-4 mr-2 shrink-0" />
              Stammdaten
            </TabsTrigger>
            <TabsTrigger value="instruments" className="data-[state=active]:bg-white shrink-0" data-testid="tab-instruments">
              <Music className="h-4 w-4 mr-2 shrink-0" />
              Instrumente ({assignedInstruments.size})
            </TabsTrigger>
            <TabsTrigger value="tariffs" className="data-[state=active]:bg-white shrink-0">
              <FileText className="h-4 w-4 mr-2 shrink-0" />
              Unterrichtsformen ({assignedTariffIds.size})
            </TabsTrigger>
            <TabsTrigger value="locations" className="data-[state=active]:bg-white shrink-0" data-testid="tab-locations">
              <MapPin className="h-4 w-4 mr-2 shrink-0" />
              Standorte ({locationsUsage?.locations?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="availability" className="data-[state=active]:bg-white shrink-0" data-testid="tab-availability">
              <CalendarClock className="h-4 w-4 mr-2 shrink-0" />
              Verfügbarkeit
            </TabsTrigger>
            <TabsTrigger value="reminders" className="data-[state=active]:bg-white shrink-0" data-testid="tab-reminders" title="Nachrichten / Erinnerungen">
              <MessageSquare className="h-4 w-4 mr-2 shrink-0" />
              Erinnerungen
            </TabsTrigger>
            <TabsTrigger value="app" className="data-[state=active]:bg-white shrink-0" data-testid="tab-app">
              App
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stammdaten">
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6" data-testid="teacher-stammdaten">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Stammdaten</h2>
                  <p className="text-sm text-slate-600">Name, Kontakt, Anschrift und Status dieser Lehrkraft.</p>
                </div>
                <Button onClick={saveMasterData} disabled={savingMaster} data-testid="teacher-stammdaten-save">
                  {savingMaster ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Speichern
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="teacher-first-name">Vorname *</Label>
                  <Input id="teacher-first-name" className="mt-1" value={form.first_name}
                    onChange={(e) => setField('first_name', e.target.value)} data-testid="teacher-edit-first-name" />
                </div>
                <div>
                  <Label htmlFor="teacher-last-name">Nachname *</Label>
                  <Input id="teacher-last-name" className="mt-1" value={form.last_name}
                    onChange={(e) => setField('last_name', e.target.value)} data-testid="teacher-edit-last-name" />
                </div>
                <div>
                  <Label htmlFor="teacher-email">E-Mail *</Label>
                  <Input id="teacher-email" type="email" className="mt-1" value={form.email}
                    onChange={(e) => setField('email', e.target.value)} data-testid="teacher-edit-email" />
                </div>
                <div>
                  <Label htmlFor="teacher-phone-edit">Telefon</Label>
                  <Input id="teacher-phone-edit" className="mt-1" value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)} data-testid="teacher-edit-phone"
                    placeholder="+49 …" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="teacher-address">Straße / Hausnummer</Label>
                  <Input id="teacher-address" className="mt-1" value={form.address}
                    onChange={(e) => setField('address', e.target.value)} data-testid="teacher-edit-address" />
                </div>
                <div>
                  <Label htmlFor="teacher-plz">PLZ</Label>
                  <Input id="teacher-plz" className="mt-1" value={form.postal_code}
                    onChange={(e) => setField('postal_code', e.target.value)} data-testid="teacher-edit-plz" />
                </div>
                <div>
                  <Label htmlFor="teacher-city">Ort</Label>
                  <Input id="teacher-city" className="mt-1" value={form.city}
                    onChange={(e) => setField('city', e.target.value)} data-testid="teacher-edit-city" />
                  <PlzOrtBridge
                    postalCode={form.postal_code}
                    city={form.city}
                    onCity={(value) => setField('city', value)}
                    testId="teacher-plz"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setField('status', v)}>
                    <SelectTrigger className="mt-1" data-testid="teacher-edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktiv</SelectItem>
                      <SelectItem value="inactive">Inaktiv</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="teacher-hourly">Stundensatz (€, optional)</Label>
                  <Input id="teacher-hourly" className="mt-1" value={form.hourly_rate}
                    onChange={(e) => setField('hourly_rate', e.target.value)} data-testid="teacher-edit-hourly"
                    placeholder="z. B. 32,50" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="teacher-qualifications">Qualifikationen</Label>
                  <Textarea id="teacher-qualifications" className="mt-1" rows={2} value={form.qualifications}
                    onChange={(e) => setField('qualifications', e.target.value)} data-testid="teacher-edit-qualifications" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="teacher-notes">Notizen</Label>
                  <Textarea id="teacher-notes" className="mt-1" rows={3} value={form.notes}
                    onChange={(e) => setField('notes', e.target.value)} data-testid="teacher-edit-notes" />
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="teacher-bank-hint">
                <div className="flex items-start gap-3">
                  <Euro className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900">Bankverbindung / Honorar</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      IBAN, BIC und Kontoinhaber liegen im Honorar-Mitarbeiterprofil
                      (Honorare &amp; Gehälter), nicht in den Lehrer-Stammdaten.
                    </p>
                    {payrollProfile ? (
                      <p className="text-sm text-slate-700 mt-2 font-mono">
                        {payrollProfile.iban
                          ? `IBAN: ${payrollProfile.iban}${payrollProfile.bic ? ` · BIC: ${payrollProfile.bic}` : ''}`
                          : 'Noch keine IBAN im Honorar-Profil hinterlegt.'}
                      </p>
                    ) : (
                      <p className="text-sm text-amber-800 mt-2">
                        Noch kein Honorar-Profil verknüpft — unter Honorare „Profile aus Lehrkräften anlegen“.
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => navigate('/payroll?tab=profiles')}
                      data-testid="teacher-open-payroll"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                      Zu Honorare &amp; Gehälter
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Instruments Tab */}
          <TabsContent value="instruments">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Instrumente zuweisen</h2>
                  <p className="text-sm text-slate-600">
                    Wählen Sie die Instrumente, die dieser Lehrer unterrichtet — alle dazugehörigen Unterrichtsformen
                    (Einzel 30/45 Min, Gruppen, Karten/Abos…) werden automatisch mit eingeschlossen.
                  </p>
                  {assignedInstruments.size > 0 && (
                    <p className="text-xs text-emerald-700 mt-2 font-medium" data-testid="instruments-summary">
                      ✓ {assignedInstruments.size} Instrumente · {totalSubjectsForAssigned} Unterrichtsformen abgedeckt
                    </p>
                  )}
                </div>
                <Button onClick={saveAssignments} disabled={saving} data-testid="save-instruments-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Speichern
                </Button>
              </div>

              {/* Zugewiesene Instrumente sofort sichtbar als Chips */}
              {assignedInstruments.size > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2.5" data-testid="assigned-instruments-bar">
                  <span className="text-xs font-semibold text-emerald-800 mr-1">Zugewiesen:</span>
                  {Array.from(assignedInstruments).sort((a, b) => a.localeCompare(b, 'de')).map((name) => (
                    <button
                      key={name}
                      onClick={() => toggleInstrument(name)}
                      title={`${name} – Klick zum Entfernen`}
                      className="group inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-xs font-medium pl-3 pr-2 py-1 hover:bg-rose-600 transition-colors"
                      data-testid={`assigned-instrument-chip-${name}`}
                    >
                      {name}
                      <X className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}

              <input
                type="text"
                placeholder="Instrumente suchen..."
                value={instrumentSearch}
                onChange={(e) => setInstrumentSearch(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-slate-200"
                data-testid="instrument-search-input"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[28rem] overflow-y-auto">
                {filteredInstruments.map((inst) => (
                  <label
                    key={inst.name}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      assignedInstruments.has(inst.name)
                        ? 'bg-emerald-50 border-emerald-300'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`instrument-row-${inst.name}`}
                  >
                    <Checkbox
                      checked={assignedInstruments.has(inst.name)}
                      onCheckedChange={() => toggleInstrument(inst.name)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{inst.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5" title={inst.subject_names.join(', ')}>
                        {inst.subject_count} Unterrichtsform{inst.subject_count === 1 ? '' : 'en'}
                      </div>
                    </div>
                    {assignedInstruments.has(inst.name) && (
                      <Check className="h-4 w-4 text-emerald-600 mt-1 flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>

              {filteredInstruments.length === 0 && (
                <p className="text-center text-slate-500 py-8">Keine Instrumente gefunden</p>
              )}
            </div>
          </TabsContent>

          {/* Tariffs Tab */}
          <TabsContent value="tariffs">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Unterrichtsformen zuweisen</h2>
                  <p className="text-sm text-slate-600">
                    Wählen Sie die Unterrichtsformen, die dieser Lehrer anbietet
                  </p>
                </div>
                <Button onClick={saveAssignments} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Speichern
                </Button>
              </div>

              <input
                type="text"
                placeholder="Unterrichtsformen suchen..."
                value={tariffSearch}
                onChange={(e) => setTariffSearch(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                {filteredTariffs.map((tariff) => (
                  <label
                    key={tariff.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      assignedTariffIds.has(tariff.id)
                        ? 'bg-amber-50 border-amber-300'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Checkbox
                      checked={assignedTariffIds.has(tariff.id)}
                      onCheckedChange={() => toggleTariff(tariff.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-slate-700 block truncate">{tariff.name}</span>
                      {tariff.amount > 0 && (
                        <span className="text-xs text-slate-500">{tariff.amount}€/Monat</span>
                      )}
                    </div>
                    {assignedTariffIds.has(tariff.id) && (
                      <Check className="h-4 w-4 text-brand-red flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>

              {filteredTariffs.length === 0 && (
                <p className="text-center text-slate-500 py-8">Keine Unterrichtsformen gefunden</p>
              )}
            </div>
          </TabsContent>

          {/* Locations Tab — live derived from courses */}
          <TabsContent value="locations">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Standorte & Räume</h2>
                <p className="text-sm text-slate-600">
                  Diese Übersicht wird live aus den Kursen abgeleitet, die diesem Lehrer zugewiesen sind.
                </p>
              </div>

              {locationsUsage === null ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : locationsUsage.total_courses === 0 ? (
                <p className="text-slate-500 italic py-8 text-center">Diesem Lehrer sind aktuell keine Kurse mit Räumen zugewiesen.</p>
              ) : (
                <div>
                  <div className="text-xs text-slate-500 mb-3">
                    {locationsUsage.total_courses} Kurse über {locationsUsage.locations.length} {locationsUsage.locations.length === 1 ? 'Standort' : 'Standorte'} verteilt
                  </div>
                  <div className="space-y-3">
                    {locationsUsage.locations.map(loc => (
                      <div key={loc.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`teacher-loc-${loc.id}`}>
                        <button
                          onClick={() => navigate(`/locations/${loc.id}`)}
                          className="w-full flex items-center gap-3 p-3 bg-brand-red/5 hover:bg-brand-red/5 text-left transition-colors"
                        >
                          <MapPin className="h-5 w-5 text-brand-red flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{loc.name}</div>
                            {(loc.short_code || loc.city) && (
                              <div className="text-xs text-slate-500">
                                {loc.short_code && <span className="font-mono">{loc.short_code}</span>}
                                {loc.short_code && loc.city && ' · '}
                                {loc.city}
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-semibold px-3 py-1 rounded-full bg-brand-red/10 text-brand-red">
                            {loc.course_count} Kurse
                          </span>
                        </button>
                        <div className="bg-white">
                          {loc.rooms.map(rm => (
                            <div key={rm.id} className="flex items-center gap-3 px-6 py-2 border-t border-slate-100 text-sm">
                              <DoorOpen className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              <span className="flex-1 text-slate-700">{rm.name}</span>
                              <span className="text-xs text-slate-500">{rm.course_count} Kurse</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            <TeacherAvailabilityTab teacherId={id} />
          </TabsContent>

          <TabsContent value="reminders">
            <PersonRemindersPanel
              personType="teacher"
              personId={id}
              personLabel={teacher ? `${teacher.last_name}, ${teacher.first_name}` : ''}
            />
          </TabsContent>

          <TabsContent value="app">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">App</h2>
              <PersonAppKennungen personType="teacher" personId={id} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
